import { observer } from 'mobx-react-lite';
import { TextField } from '../../../../lib/components/atomic/input/textField';
import { InputFieldLabel } from '../../../../lib/components/atomic/input/shared/fieldLabel';
import { Input as MuiInput, FormHelperText, ThemeProvider } from '@mui/material';
import { useTranslation } from 'react-i18next';
import { useCreateBonus } from '../hooks/useCreateBonus';
import { useStepper } from '../../partials/actionStepperPartial';
import { ArrowForwardIconSvg } from '../../../../lib/icons';
import { useEffect, useMemo } from 'react';
import { descriptionTheme } from '../../createCustomBenefit/steps/benefitProfile';
import { Drawer } from '../../../../lib/components/atomic/dataDisplay/drawer';
import { InfoSection } from '../../../../lib/components/atomic/typography/InfoSection';
import { HintBox } from '../../../../lib/components/atomic/typography/hintBox';
import { useFormik } from 'formik';
import {
  descriptionSchema,
  TITLE_MAX_LENGTH,
  DESCRIPTION_MAX_LENGTH
} from '../utils/validationSchemas';

export const Description = observer(() => {
  const { t } = useTranslation();
  const { newBonus } = useCreateBonus();
  const {
    setNextButtonConfig,
    activeStep,
    setActiveStep,
    showInfoDrawer,
    setShowInfoDrawer,
    editMode
  } = useStepper();

  const { values, errors, touched, handleChange, submitForm } = useFormik({
    initialValues: {
      title: newBonus.title,
      description: newBonus.description ?? ''
    },
    enableReinitialize: true,
    validationSchema: descriptionSchema(t),
    onSubmit: (values) => {
      newBonus.title = values.title;
      newBonus.description = values.description ?? undefined;
      editMode ? setActiveStep(6) : setActiveStep(activeStep + 1);
    }
  });

  const nextButtonConfig = useMemo(
    () => ({
      text: editMode ? t('common:CONFIRM') : t('common:NEXT'),
      endIcon: <ArrowForwardIconSvg />,
      onClick: () => {
        submitForm();
      }
    }),
    [editMode, t, submitForm]
  );

  useEffect(() => {
    setNextButtonConfig(nextButtonConfig);
  }, [nextButtonConfig, setNextButtonConfig]);

  return (
    <>
      <div className="flex flex-col">
        <div className="-mt-2 mb-2">{t('bonus:subtitle:description')}</div>
        <div className="flex flex-col gap-y-5 mt-6">
          <div>
            <InputFieldLabel
              label={t('bonus:bonusTitle')}
              required={true}
              tooltip={t('bonus:tooltip:title')}
            />
            <TextField
              name="title"
              type="text"
              value={values.title}
              placeholder={t('bonus:namePlaceholder')}
              onChange={(e) => {
                handleChange(e);
              }}
              error={!!errors.title}
              helperText={
                errors.title && touched.title
                  ? errors.title
                  : `${values.title.length} / ${TITLE_MAX_LENGTH}`
              }
              required={true}
            />
          </div>

          <div>
            <InputFieldLabel
              label={t('benefitSet:description') + ' ' + t('common:OPTIONAL')}
              tooltip={t('bonus:tooltip:description')}
            />
            <ThemeProvider theme={descriptionTheme}>
              <MuiInput
                name="description"
                multiline={true}
                minRows={8}
                value={values.description}
                disableUnderline
                onChange={(e) => {
                  handleChange(e);
                }}
                placeholder={t('bonus:descriptionPlaceholder')}
                sx={(theme) => ({
                  borderRadius: '8px',
                  borderColor: errors.description
                    ? theme.palette.error.main
                    : theme.palette.grey['200'],
                  padding: '12px'
                })}
              />
              <FormHelperText
                sx={(theme) => ({
                  marginLeft: '12px',
                  // eslint-disable-next-line no-extra-boolean-cast
                  color: !!errors.description ? theme.palette.error.main : theme.palette.grey[600]
                })}
              >
                {errors.description ??
                  `${values.description?.length ?? 0} / ${DESCRIPTION_MAX_LENGTH}`}
              </FormHelperText>
            </ThemeProvider>
          </div>
        </div>
      </div>
      <Drawer
        label={t('bonus:descriptionDrawer:title')}
        open={showInfoDrawer}
        closeDrawer={() => setShowInfoDrawer(false)}
      >
        <div className="flex flex-col gap-y-6">
          <InfoSection content={t('bonus:descriptionDrawer:texts', { returnObjects: true })} />
          <HintBox description={t('bonus:descriptionDrawer:hint', { returnObjects: true })} />
        </div>
      </Drawer>
    </>
  );
});
